import React, { useEffect } from 'react';
import { BaseCss } from '@charlietango/ui';
import { CacheProvider, Global, ThemeProvider } from '@emotion/react';
import { EmotionCache } from '@emotion/cache';

import { AuthProvider } from '../api/AuthProvider';
import usePageData, { PageResult } from '../api/hooks/usePageData';
import { prefetchParties } from '../api/hooks/useParties';
import AuthMessages from '../components/AuthMessages/AuthMessages';
import StatusMessages from '../components/StatusMessages/StatusMessages';
import { DictionaryProvider } from '../hooks/useDictionary';
import { globalStyles } from '../styles/global-styles';
import theme from '../styles/theme';
import { HtmlConfigViewModel } from '../view-models/HtmlConfigViewModel';
import DocumentTitle from './DocumentTitle';
import { Modules } from './Modules';
import { PartyDetailsUrlProvider } from './PartyDetailsUrlContext';
import { useRouter } from './Router';
import ScrollRestoration from './ScrollRestoration';
import { emotionCache } from '../styles/emotion-cache';

type Props = {
  config: HtmlConfigViewModel;
  emotionCache?: EmotionCache;
  initialData: PageResult;
};

function App({
  initialData,
  config,
  emotionCache: cache = emotionCache,
}: Props) {
  const { pathname } = useRouter();
  const { data } = usePageData(pathname, {
    initialData,
  });

  useEffect(() => {
    // Regardless of the page we land on, start prefetching the parties so they are ready.
    prefetchParties();
  }, []);

  return (
    <CacheProvider value={cache}>
      <AuthProvider
        authCookie={config.authCookie}
        loginUrl={config.loginUrl}
        logoutUrl={config.logoutUrl}
      >
        <DictionaryProvider items={config.dictionary || {}}>
          <DocumentTitle />
          <ThemeProvider theme={theme}>
            {/* We only need to render the BaseCss on the server, since the global styles shouldn't change. If they could (e.g. the theme changes), then always output the <BaseCss> */}
            {process.env.SERVER ? (
              <>
                <Global styles={globalStyles} />
                <BaseCss />
              </>
            ) : (
              // Likewise we only need to render ScrollRestoration on the client
              <ScrollRestoration />
            )}
            <PartyDetailsUrlProvider>
              {data && <Modules modules={data.modules} id={pathname} />}
            </PartyDetailsUrlProvider>
            <StatusMessages />
            <AuthMessages />
          </ThemeProvider>
        </DictionaryProvider>
      </AuthProvider>
    </CacheProvider>
  );
}

export default App;
